/**
 * Subscription Plans
 *
 * Six tiers ordered lowest to highest.
 * Billing now writes this field — see packages/shared/src/billing/prices.ts
 * for the price table.
 */

export const SUBSCRIPTION_PLANS = ['free', 'lite', 'pro', 'max', 'ultra', 'custom'] as const;
export type SubscriptionPlan = typeof SUBSCRIPTION_PLANS[number];

/**
 * Plans ordered by tier level (index = level).
 * Used for >= comparisons (e.g. "pro and above").
 */
export const PLAN_LEVELS: Record<SubscriptionPlan, number> = {
  free: 0,
  lite: 1,
  pro: 2,
  max: 3,
  ultra: 4,
  custom: 5,
};

/**
 * The machine each tier buys, in MB — the "8GB/8GB/16GB/32GB RAM machine"
 * lines sold on /pricing and the plan page (TIER_FEATURES quotes them). Every
 * sandbox spec derives its memory from here; before this table existed every
 * plan got the hardcoded 4096 the copy only promised to Lite. The floor is 8GB
 * for EVERY plan since 2026-09-03: a 4GB box with a browser open (~2.5GB) could
 * not build a landing page — the kernel OOM-killed the agent mid `npm install`
 * — and "if we can't reliably build a landing page, what's the point" (the
 * founder). A live sandbox converges to this on its next wake (plan-memory.ts
 * drift → PUT). `custom` is negotiated per contract; it starts at the Ultra
 * machine rather than below it.
 */
export const PLAN_SANDBOX_MEMORY_MB: Record<SubscriptionPlan, number> = {
  free: 8192,
  lite: 8192,
  pro: 8192,
  max: 16384,
  ultra: 32768,
  custom: 32768,
};

/**
 * The machine each tier STARTS on once SANDBOX_MEMORY_AUTOGROW_V1 is armed, in MB.
 *
 * The 8GB floor above is paid for on every box the moment it wakes, including the
 * overwhelming majority that never come near it — a Lite user writing a document
 * does not need the machine that builds a landing page. So the bottom two tiers
 * start at 4GB and EARN the 8GB: the server watches the memory the runner reports
 * on the lease beat and, after twelve consecutive beats under 25% free, writes a
 * seven-day override that the drift gate applies at the next idle boundary
 * (sandbox/memory-pressure.ts → sandbox/plan-memory.ts).
 *
 * Pro and above are unchanged — those tiers are SOLD the big machine, and a table
 * that shrank them would break the copy. This table is only consulted while the
 * flag is on; with it off PLAN_SANDBOX_MEMORY_MB above is the whole truth, which
 * is what makes the rollout reversible.
 */
export const PLAN_SANDBOX_MEMORY_MB_SMALL_START: Record<SubscriptionPlan, number> = {
  free: 4096,
  lite: 4096,
  pro: 8192,
  max: 16384,
  ultra: 32768,
  custom: 32768,
};

/**
 * The volume every new user's workspace starts at, in MB, once VOLUME_AUTOGROW_V1
 * is armed.
 *
 * Volumes were created at a flat 4GB for everyone, paid for from the first login
 * whether or not a byte was ever written — and Phase 2 has just moved every install
 * and package cache ONTO the volume, so the flat number is now both too generous for
 * the median user and too small for the one running `pip install torch`. So a volume
 * starts at half of it and GROWS: the server watches the fill the runner reports on
 * the lease beat and doubles the volume, tidy-first, up to the plan's ceiling below
 * (sandbox/volume-grow.ts). Blaxel grows an ATTACHED volume live — the spike watched
 * `df` inside the box report the new size within 15s of the updateVolume, with no
 * restart — so growth costs the user nothing.
 *
 * Consulted only while the flag is on; with it off the flat 4096 is the whole truth,
 * which is what makes the rollout reversible.
 */
export const VOLUME_INITIAL_MB = 2048;

/**
 * How far a plan's volume may grow, in MB — the ceiling on the doubling above.
 *
 * A ceiling, not an allowance: nothing here is spent until the box actually fills,
 * and most volumes never leave VOLUME_INITIAL_MB. The paid tiers are what buy the room
 * a real build needs; `custom` is negotiated per contract and starts at the Ultra
 * ceiling.
 *
 * The bottom two were 4096 — the flat size every volume used to be created at — until
 * 2026-09-13, when two prod boxes filled that 4 GB and every prod volume was grown to
 * 8 GB by hand. A ceiling below what the fleet already runs on is a ceiling that stops
 * autogrow one doubling short of the box the user is standing in, so the bottom tiers
 * now reach what the ops move already gave them. The growth still costs nothing until
 * a box actually fills, and the tidy gate still runs first.
 */
export const PLAN_VOLUME_CEILING_MB: Record<SubscriptionPlan, number> = {
  free: 8192,
  lite: 8192,
  pro: 8192,
  max: 16384,
  ultra: 32768,
  custom: 32768,
};

/**
 * Returns true if the user's plan meets or exceeds the required plan tier.
 *
 * @example
 * hasMinimumPlan('pro', 'pro')    // true
 * hasMinimumPlan('max', 'pro')    // true
 * hasMinimumPlan('free', 'pro')   // false
 */
export function hasMinimumPlan(
  userPlan: SubscriptionPlan,
  requiredPlan: SubscriptionPlan,
): boolean {
  return PLAN_LEVELS[userPlan] >= PLAN_LEVELS[requiredPlan];
}

/**
 * Human-readable label for each plan.
 */
export const PLAN_LABELS: Record<SubscriptionPlan, string> = {
  free: 'Free',
  lite: 'Lite',
  pro: 'Pro',
  max: 'Max',
  ultra: 'Ultra',
  custom: 'Custom',
};
